import { Modal, Row, Col, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import axiosInstance from "../../../../services/AxiosInstance";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";
import LoadingIcon from "../../../components/Icon/LoadingIcon";
import { convertDoubleToStringPTBR } from "../../../../utils/ConverterUtils";
import ModalCloseButton from "../../../components/Common/Modal/ModalCloseButton";

const CatalogDetailsModal = ({ show, onHide, itemId, type }) => {
    const { t } = useTranslation();
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [itemData, setItemData] = useState(null);

    useEffect(() => {
        if (show && itemId) {
            setLoading(true);
            setImageUrl(null);
            setItemData(null);

            // Produto e serviço compartilham a API do catálogo; o tipo recebido
            // define apenas a apresentação e as regras exibidas neste modal.
            axiosInstance.get(Api.main.product + "/" + itemId)
                .then(resp => {
                    setItemData(resp.data);
                    
                    // Tenta buscar a imagem
                    if (resp.data.uploadedFileLocation) {
                        return axiosInstance.get(Api.main.product + "/download/base64/" + itemId);
                    }
                    return null;
                })
                .then(imageResp => {
                    if (imageResp && imageResp.data) {
                        setImageUrl("data:image/jpeg;base64," + imageResp.data);
                    }
                })
                .catch(error => {
                    ApiError(error);
                })
                .finally(() => setLoading(false));
        }
    }, [show, itemId]);

    const formatCurrency = (value) => {
        if (!value) return "R$ 0,00";
        return "R$ " + convertDoubleToStringPTBR(value);
    };

    const formatPercentage = (value) => {
        if (!value) return "0,00%";
        return convertDoubleToStringPTBR(value) + "%";
    };

    const isService = (itemData?.type || type) === 'SERVICE';
    // O item possui uma única categoria; a lista de secundárias foi removida do catálogo.
    const category = itemData?.category;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered className="catalog-details-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fas fa-circle-info me-2"></i>
                    {t(isService ? 'service_details' : 'product_details')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="py-5 text-center">
                        <LoadingIcon className="fa-3x" />
                        <p className="mt-3">{t('loading')}...</p>
                    </div>
                ) : itemData ? (
                    <Row>
                        {/* Coluna da Imagem */}
                        <Col md={5} className="mb-3">
                            <div className="d-flex flex-column align-items-center">
                                {imageUrl ? (
                                    <img 
                                        src={imageUrl} 
                                        alt={itemData.name} 
                                        className="img-fluid rounded shadow"
                                        style={{ maxHeight: '350px', objectFit: 'cover', width: '100%' }}
                                    />
                                ) : (
                                    <div className="catalog-details-placeholder d-flex flex-column align-items-center justify-content-center rounded p-5" style={{ minHeight: '350px', width: '100%' }}>
                                        <i className="fas fa-image fa-4x text-muted mb-3"></i>
                                        <p className="text-muted">{t('no_image_available')}</p>
                                    </div>
                                )}
                            </div>
                        </Col>

                        {/* Coluna dos Detalhes */}
                        <Col md={7}>
                            <div className="catalog-details">
                                {/* Nome e Status */}
                                <div className="mb-3">
                                    <h4 className="catalog-details-name mb-2">{itemData.name}</h4>
                                    <Badge bg={itemData.status === 'ACTIVE' ? 'success' : 'secondary'}>
                                        {t(itemData.status.toLowerCase())}
                                    </Badge>
                                </div>

                                {/* Preço */}
                                <div className="catalog-details-price mb-3 p-3 rounded">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-muted">{t('price')}</span>
                                        <h3 className="catalog-details-value mb-0">{formatCurrency(itemData.price)}</h3>
                                    </div>
                                </div>

                                {/* Informações básicas */}
                                <div className="mb-3">
                                    <Row>
                                        {itemData.barCode && (
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">{t('bar_code')}</small>
                                                <strong>{itemData.barCode}</strong>
                                            </Col>
                                        )}
                                        {itemData.refCode && (
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">{t('ref_code')}</small>
                                                <strong>{itemData.refCode}</strong>
                                            </Col>
                                        )}
                                        {itemData.unit && (
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">{t('unit')}</small>
                                                <strong>{t('unit_measure.' + itemData.unit.toLowerCase())}</strong>
                                            </Col>
                                        )}
                                        {!isService && (
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">{t('track_stock')}</small>
                                                <strong>{itemData.trackStock ? t('yes') : t('no')}</strong>
                                            </Col>
                                        )}
                                    </Row>
                                </div>

                                {/* Custos e Margens */}
                                {!isService && (itemData.costPrice > 0 || itemData.profitMargin > 0) && (
                                    <div className="mb-3">
                                        <h6 className="catalog-details-section-title border-bottom pb-2 mb-2">
                                            <i className="fas fa-calculator me-2"></i>
                                            {t('costs_and_margins')}
                                        </h6>
                                        <Row className="small">
                                            {itemData.costPrice > 0 && (
                                                <Col md={6} className="mb-2">
                                                    <span className="text-muted d-block">{t('cost_price')}</span>
                                                    <strong>{formatCurrency(itemData.costPrice)}</strong>
                                                </Col>
                                            )}
                                            {itemData.profitMargin > 0 && (
                                                <Col md={6} className="mb-2">
                                                    <span className="text-muted d-block">{t('profit_margin')}</span>
                                                    <strong>{formatPercentage(itemData.profitMargin)}</strong>
                                                </Col>
                                            )}
                                            {itemData.icms > 0 && (
                                                <Col md={6} className="mb-2">
                                                    <span className="text-muted d-block">{t('icms')}</span>
                                                    <strong>{formatPercentage(itemData.icms)}</strong>
                                                </Col>
                                            )}
                                            {itemData.suggestedPrice > 0 && (
                                                <Col md={6} className="mb-2">
                                                    <span className="text-muted d-block">{t('suggested_price')}</span>
                                                    <strong>{formatCurrency(itemData.suggestedPrice)}</strong>
                                                </Col>
                                            )}
                                        </Row>
                                    </div>
                                )}

                                {/* Categoria */}
                                {category && (
                                    <div className="mb-3">
                                        <small className="text-muted d-block mb-2">{t('category')}</small>
                                        <div className="d-flex flex-wrap gap-2">
                                            <Badge className="catalog-details-category px-3 py-2">
                                                {category.name}
                                            </Badge>
                                        </div>
                                    </div>
                                )}

                                {/* Fornecedor */}
                                {!isService && itemData.vendor && (
                                    <div className="mb-3">
                                        <small className="text-muted d-block">{t('vendor')}</small>
                                        <strong>{itemData.vendor.name}</strong>
                                    </div>
                                )}

                                {/* Descrição/Detalhe */}
                                {itemData.detail && (
                                    <div className="mb-3">
                                        <small className="text-muted d-block mb-2">{t('detail')}</small>
                                        <p className="text-muted small mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                            {itemData.detail}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <div className="py-5 text-center">
                        <i className="fas fa-exclamation-circle fa-4x text-muted mb-3"></i>
                        <p className="text-muted">{t(isService ? 'service_not_found' : 'product_not_found')}</p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <ModalCloseButton onClick={onHide} />
            </Modal.Footer>
        </Modal>
    );
};

export default CatalogDetailsModal;
